import React, { CSSProperties, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { VelavuSite } from "velavu-js-api";
import IconFloors from "../../dynamicicons/icon-floors";
import IconPlace from "../../dynamicicons/icon-place";
import Divider from "../../elements/divider";
import VelavuMenu from "../../elements/velavu-menu";
import { useToggleable } from "../../helper/hook-helper";
import { mapFloorNumber } from "../../helper/language-helper";
import * as styles from "./selector-site.module.scss";
import whitelabel from "../../whitelabel";

interface SelectorSiteProps {
	style?: CSSProperties;
	className?: string;

	site: VelavuSite | undefined | "select";
	onClick?: VoidFunction;

	selectedFloorID?: string | undefined;
	onClickFloor?: (floorID: string | undefined) => void;
}

function FloorMenuItem(props: { label: string; onClick?: VoidFunction }) {
	return (
		<button className={styles.menuItem} onClick={props.onClick}>
			<span className={styles.menuItemLabel}>{props.label}</span>
		</button>
	);
}

export default function SelectorSite(props: SelectorSiteProps) {
	const { t } = useTranslation();
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const [isFloorMenu, openFloorMenu, closeFloorMenu] = useToggleable();

	const propsOnClickFloor = props.onClickFloor;
	const selectFloor = useCallback(
		(floorID: string | undefined) => {
			closeFloorMenu();
			if (propsOnClickFloor) propsOnClickFloor(floorID);
		},
		[propsOnClickFloor, closeFloorMenu],
	);

	const selectedFloorLabel = useMemo(() => {
		if (props.selectedFloorID === undefined) {
			return mapFloorNumber(undefined, t);
		}
		if (typeof props.site !== "object" || props.site.floors === undefined) {
			return mapFloorNumber(undefined, t);
		}
		const floor = props.site.floors.find(
			(entry) => entry.id === props.selectedFloorID,
		);
		return mapFloorNumber(floor?.level, t);
	}, [props.selectedFloorID, props.site, t]);

	const displayFloors = useMemo(() => {
		if (typeof props.site !== "object" || props.site.floors === undefined) {
			return [];
		}

		//Sort in descending order
		return [...props.site.floors].sort((f1, f2) => f2.level - f1.level);
	}, [props.site]);

	return (
		<div
			style={props.style}
			className={`${props.className} ${styles.container}`}
		>
			<button className={styles.siteButton} onClick={props.onClick}>
				<IconPlace className={styles.icon} size={16} color="unset" />
				<span className={styles.label}>
					{props.site === "select"
						? t("shared.site_switcher.select_site")
						: (props.site?.name ?? t("common.global"))}
				</span>
			</button>

			{typeof props.site === "object" && (
				<>
					<Divider orientation="vertical" />

					<button
						ref={buttonRef}
						className={styles.floorButton}
						onClick={openFloorMenu}
					>
						<IconFloors color={whitelabel.palette.primary900} />
						<span>{selectedFloorLabel}</span>
					</button>

					<VelavuMenu
						className={styles.floorMenu}
						open={isFloorMenu}
						onClose={closeFloorMenu}
						refPosition={buttonRef.current}
					>
						<FloorMenuItem
							label={mapFloorNumber(undefined, t)}
							onClick={() => selectFloor(undefined)}
						/>
						{props.site.floors !== undefined &&
							props.site.floors.length > 0 && (
								<>
									<Divider edgePadding />
									{displayFloors.map((floor) => (
										<FloorMenuItem
											key={floor.id}
											label={mapFloorNumber(
												floor.level,
												t,
											)}
											onClick={() =>
												selectFloor(floor.id)
											}
										/>
									))}
								</>
							)}
					</VelavuMenu>
				</>
			)}
		</div>
	);
}
